import { ColumnsType } from "antd/es/table";
import { PendingCorrection } from "../../types/grading";
import { Translation } from "react-i18next";
import dayjs from "dayjs";
import { Progress, Avatar, Tooltip, Button, Space } from "antd";
import { Clock, User, FileText, Calendar, UserPlus, Users } from "lucide-react";
import { PendingCorrectionActions } from "../../components/grading/PendingCorrectionActions";
import { GradingStatusBadge } from "../../components/grading/GradingStatusBadge";

const initials = (name: string) =>
  name
    .split(" ")
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

interface ColumnOptions {
  canAssign: boolean;
  onAssign: (record: PendingCorrection) => void;
}

export const getPendingCorrectionsColumns = ({
  canAssign,
  onAssign,
}: ColumnOptions): ColumnsType<PendingCorrection> => [
  {
    title: <Translation>{(t) => t("exam")}</Translation>,
    dataIndex: "examTitle",
    key: "examTitle",
    sorter: (a, b) => a.examTitle.localeCompare(b.examTitle),
    render: (text) => (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <FileText size={16} />
        {text}
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("examTerm")}</Translation>,
    dataIndex: "examTermDate",
    key: "examTermDate",
    sorter: (a, b) =>
      dayjs(a.examTermDate).valueOf() - dayjs(b.examTermDate).valueOf(),
    render: (date) => (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Calendar size={16} />
        {date ? dayjs(date).format("DD.MM.YYYY") : "-"}
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("participant")}</Translation>,
    dataIndex: "participantName",
    key: "participantName",
    render: (text, record) => (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <User size={16} />
          {text}
        </div>
        <span style={{ fontSize: "12px", color: "gray", marginLeft: "24px" }}>
          {record.participantEmail}
        </span>
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("submittedAt")}</Translation>,
    dataIndex: "submissionDate",
    key: "submissionDate",
    sorter: (a, b) =>
      dayjs(a.submissionDate).valueOf() - dayjs(b.submissionDate).valueOf(),
    defaultSortOrder: "ascend",
    render: (date) => (
       <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Clock size={16} />
        {date ? dayjs(date).format("DD.MM.YYYY HH:mm") : "-"}
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("status")}</Translation>,
    dataIndex: "gradingStatus",
    key: "gradingStatus",
    render: (status, record) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <GradingStatusBadge status={status} />
        {record.activeGraders && record.activeGraders.length > 0 && (
          <Translation>
            {(t) => (
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#1677ff" }}>
                <Users size={12} />
                {t("grading.currentlyGrading", {
                  names: record.activeGraders.map((g) => g.assessorName).join(", "),
                })}
              </span>
            )}
          </Translation>
        )}
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("grading.assignedAssessors")}</Translation>,
    key: "assignedAssessors",
    render: (_, record) =>
      record.assignedAssessors && record.assignedAssessors.length > 0 ? (
        <Avatar.Group max={{ count: 3 }} size="small">
          {record.assignedAssessors.map((a) => (
            <Tooltip key={a.assessorId} title={a.assessorName}>
              <Avatar size="small" style={{ backgroundColor: "#F1B80E", color: "#2b2200" }}>
                {initials(a.assessorName)}
              </Avatar>
            </Tooltip>
          ))}
        </Avatar.Group>
      ) : (
        <Translation>
          {(t) => <span style={{ color: "#bfbfbf", fontSize: 12 }}>{t("grading.notAssigned")}</span>}
        </Translation>
      ),
  },
  {
    title: <Translation>{(t) => t("progress")}</Translation>,
    dataIndex: "progressPercentage",
    key: "progressPercentage",
    render: (percent, record) => (
      <div style={{ width: 150 }}>
        <Progress percent={Math.round(percent)} size="small" />
        <div style={{ fontSize: "11px", color: "gray" }}>
          {record.gradedQuestions} / {record.totalQuestionsToGrade} graded
        </div>
      </div>
    ),
  },
  {
    title: <Translation>{(t) => t("actions")}</Translation>,
    key: "actions",
    render: (_, record) => (
      <Space onClick={(e) => e.stopPropagation()}>
        {canAssign && (
          <Translation>
            {(t) => (
              <Tooltip title={t("grading.assign.title")}>
                <Button
                  shape="circle"
                  icon={<UserPlus size={16} />}
                  onClick={() => onAssign(record)}
                />
              </Tooltip>
            )}
          </Translation>
        )}
        <PendingCorrectionActions correction={record} />
      </Space>
    ),
  },
];
